import { useEffect, useState } from "react";
import cx from "classnames";

import { Optional, WithStringValues } from "common/utils/types";
import { Availability } from "common/types/catalog";

import Carousel from "client/common/components/interactive/Carousel";
import { MediaItem, MediaVideo, MediaImage } from "client/common/types";

import { IProductItem, IProductLineDetails, ProductDetailSearch } from "../types";
import { useProductLineAvailabilityQuery } from "../hooks";

import styles from "../styles/ProductLineMedia.module.less";

type UnavailableOverlayProps = {
  productLine: IProductLineDetails;
  search: WithStringValues<ProductDetailSearch>;
};

const UnavailableOverlay = ({ productLine, search }: UnavailableOverlayProps) => {
  const { data: availability, isLoading: isLoadingAvailability } = useProductLineAvailabilityQuery(
    productLine.id,
    {
      product: search.product,
      item: search.item,
    },
  );

  if (isLoadingAvailability) {
    return null;
  }

  return (
    <div className={styles.unavailableOverlay}>
      <div className={styles.text}>
        {availability?.availability === Availability.Discontinued
          ? "Product discontinued"
          : "Product unavailable"}
      </div>
    </div>
  );
};

type ProductLineMediaProps = {
  productLine: IProductLineDetails;
  item: Optional<IProductItem>;
  className: string;
  search: WithStringValues<ProductDetailSearch>;
  isAvailable: boolean;
};

const ProductLineMedia = ({
  productLine,
  item,
  className,
  search,
  isAvailable,
}: ProductLineMediaProps) => {
  const [selectedIndex, setSelectedIndex] = useState(0);

  useEffect(() => {
    // Reset without remounting the component when the item changes
    setSelectedIndex(0);
  }, [item?.id]);

  const images = (() => {
    if (item?.images.length) {
      return item.images;
    }
    if (productLine.image) {
      return [productLine.image];
    }
    return [];
  })();

  const medias: MediaItem[] = [
    ...(images.map<MediaImage>((image) => ({
      key: `${image.id}`,
      kind: "image",
      thumbnailUrl: image.thumbnail_url,
      contentUrl: image.large_url,
      alt: productLine.brand_name,
    })) ?? []),
    ...productLine.videos.map<MediaVideo>((video) => ({
      key: `${video.id}`,
      kind: "video",
      previewImageUrl: video.thumbnail_url,
      videoUrl: video.video_url,
      alt: productLine.brand_name,
    })),
  ];

  const fallback: MediaItem[] = [
    {
      key: "fallback",
      kind: "image",
      // Will automatically use the default image
      thumbnailUrl: "",
      contentUrl: "",
      alt: productLine.brand_name,
    },
  ];

  return (
    <div className={cx(className, !medias.length && styles.empty)}>
      <Carousel
        selectedIndex={selectedIndex}
        onSelectIndex={setSelectedIndex}
        items={medias.length ? medias : fallback}
      />
      {productLine.has_multiple_manufacturers && (
        <p className={styles.disclaimer}>The actual product may vary from the image shown above</p>
      )}
      {productLine.is_compound && (
        <p className={styles.disclaimer}>
          Product image is for illustrative purposes only. Actual product, including container,
          size, color, shape, markings, or branding, may vary.
        </p>
      )}
      {!isAvailable && <UnavailableOverlay productLine={productLine} search={search} />}
    </div>
  );
};

export default ProductLineMedia;
